// =============================================================================
// handlers.ts — Bridges NGW ServiceContext to MSaaS.Plugin handler callbacks
// =============================================================================

import type { MediaSyncHandlers } from './config';
import type { MediaUploadRequest } from '../../contexts/ServiceContext';

const MEDIA_TYPES: Record<string, number> = {
  PHOTO: 50000967225,
  DOCUMENT: 50062708916,
  FLOOR_PLAN: 50070587985,
  TOUR_URL: 10000000365,
};

interface ServiceDeps {
  searchListingsPaginated: (
    request: {
      page: number;
      perPage: number;
      streamResults?: boolean;
      filters?: Record<string, any>;
    },
    onSummary?: (response: any) => void,
    onDetail?: (response: any) => void,
    onComplete?: () => void,
    onError?: (error: Error) => void,
  ) => Promise<void>;
  searchListings: (query?: string) => Promise<{
    ResponseStatus: { StatusCode: string; StatusMessage: string };
    ResponseString: { Listings: any[] };
  }>;
  isWebSocketReady: () => boolean;
  getListing: (
    listingKey: string,
    options?: { mediaV2?: boolean },
  ) => Promise<any>;
  initiateMediaUpload: (
    requests: MediaUploadRequest[],
  ) => Promise<Array<{ uploadUrl: string; jobId?: string }>>;
  saveListingTour: (
    listingKey: string,
    name: string,
    url: string,
  ) => Promise<any>;
  saveListing: (
    action: 'CREATE' | 'UPDATE' | 'DELETE',
    listingKey: string | undefined,
    listing: any,
    options?: { debug?: boolean },
  ) => Promise<any>;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

/**
 * Normalizes listing data from either the UPPER_SNAKE_CASE WebSocket response
 * or camelCase/PascalCase REST response into the shape MSaaS.Plugin expects.
 */
function mapListingData(d: Record<string, unknown>): void {
  d.listingKey = d.LISTING_KEY ?? d.ListingKey ?? d.listingKey;
  d.listingId = d.LISTING_ID ?? d.ListingId ?? d.listingId;
  d.city = d.CITY ?? d.City ?? d.city;

  const streetNumber =
    (d.STREET_NUMBER as string) ?? (d.StreetNumber as string) ?? '';
  const streetName =
    (d.STREET_NAME as string) ?? (d.StreetName as string) ?? '';
  const streetDirSuffix =
    (d.STREET_DIR_SUFFIX as string) ?? (d.StreetDirSuffix as string) ?? '';
  const streetSuffix =
    (d.STREET_SUFFIX as string) ?? (d.StreetSuffix as string) ?? '';
  const builtAddress = [streetNumber, streetName, streetSuffix, streetDirSuffix]
    .filter((part) => part && part !== 'null' && part !== 'undefined')
    .join(' ')
    .trim();

  d.fullStreetAddress =
    (d.FullStreetAddress as string)?.split(',')?.[0] ??
    (builtAddress || (d.UNPARSED_ADDRESS as string)?.split(',')?.[0]) ??
    '';

  d.state =
    (d.STATE_OR_PROVINCE as string) ??
    (d.StateOrProvince as { value?: string })?.value ??
    (d.StateOrProvince as string) ??
    d.state;
  d.zip = d.POSTAL_CODE ?? d.PostalCode ?? d.zip;
  d.type =
    (d.PROPERTY_TYPE as string) ??
    (d.PropertyType as { value?: string })?.value ??
    (d.PropertyType as string) ??
    d.type;
  d.imageUrl =
    (d.__summary__ as Record<string, unknown>)?.LSumListPictureURL ??
    d.MediaURL ??
    d.imageUrl;

  const isDraft = d.LiveListing === false || d.__in_workspace__ === true;
  const media = d.Media as any[] | undefined;
  const hasMedia =
    (media && media.length > 0) ||
    d.LisMediaList ||
    d.TotalPhotos ||
    d.PhotosCount;

  if (isDraft && !hasMedia) {
    d.sortOrder = 400;
  } else if (isDraft) {
    d.sortOrder = 300;
  } else if (!hasMedia) {
    d.sortOrder = 200;
    d.isPublished = true;
  } else {
    d.sortOrder = 100;
    d.isPublished = true;
  }

  const PHOTO_MEDIA_TYPE = 50000967225;
  const resources = d.__resources__ as
    | { Media?: Array<Record<string, unknown>> }
    | undefined;
  if (resources?.Media && Array.isArray(resources.Media)) {
    d.existingPhotoCount = resources.Media.filter(
      (item) =>
        Number((item.MediaCategory as { key?: unknown })?.key) ===
        PHOTO_MEDIA_TYPE,
    ).length;
  } else if (media && Array.isArray(media)) {
    d.existingPhotoCount = media.length;
  } else {
    const tp = d.TotalPhotos ?? d.PhotosCount;
    d.existingPhotoCount =
      typeof tp === 'number' && !Number.isNaN(tp)
        ? tp
        : parseInt(String(tp), 10) || 0;
  }
}

/**
 * Converts manifest string media types (e.g. "photo") to numeric IDs.
 */
function transform(mediaFile: Record<string, unknown>): void {
  const manifestMediaType = String(mediaFile.mediaType).toUpperCase();
  const mapped = MEDIA_TYPES[manifestMediaType];
  if (mapped !== undefined) {
    mediaFile.mediaType = mapped;
  }
}

/**
 * Maps a numeric or string media type to the category label expected by the
 * NGW media-start-upload endpoint.
 */
function toCategory(mediaType: string | number): string {
  const mt =
    typeof mediaType === 'string' ? mediaType.toUpperCase() : mediaType;
  switch (mt) {
    case 'PHOTO':
    case MEDIA_TYPES.PHOTO:
      return 'Photo';
    case 'DOCUMENT':
    case MEDIA_TYPES.DOCUMENT:
      return 'Document';
    case 'FLOOR_PLAN':
    case MEDIA_TYPES.FLOOR_PLAN:
      return 'Floor Plan';
    case 'TOUR_URL':
    case MEDIA_TYPES.TOUR_URL:
      return 'Tour';
    default:
      return 'Photo';
  }
}

// -----------------------------------------------------------------------------
// Handler factory
// -----------------------------------------------------------------------------

export function createNgwHandlers(deps: ServiceDeps): MediaSyncHandlers {
  const {
    searchListingsPaginated,
    searchListings,
    isWebSocketReady,
    getListing,
    initiateMediaUpload,
    saveListingTour,
    saveListing,
  } = deps;

  return {
    // -----------------------------------------------------------------
    // fetchListings — stream agent listings to the plugin
    // -----------------------------------------------------------------
    fetchListings: async (pushListing) => {
      try {
        if (isWebSocketReady()) {
          await new Promise<void>((resolve, reject) => {
            searchListingsPaginated(
              { page: 1, perPage: 250, streamResults: true },
              (summary) => {
                const listings = summary?.summary?.listings;
                if (!listings || !Array.isArray(listings)) return;

                for (const item of listings) {
                  const listing: Record<string, unknown> = {
                    ...item,
                    ...item.Property,
                  };
                  mapListingData(listing);
                  if (!listing.listingKey || !listing.listingId) continue;
                  pushListing(listing, false);
                }
              },
              (detail) => {
                const listing: Record<string, unknown> = {
                  ...detail,
                  ...(detail.Property ?? {}),
                };
                delete listing.status;
                mapListingData(listing);
                if (!listing.listingKey || !listing.listingId) return;
                pushListing(listing, false);
              },
              () => resolve(),
              (error) => reject(error),
            );
          });
        } else {
          const result = await searchListings();
          if (!result?.ResponseString?.Listings) return;

          for (const listing of result.ResponseString.Listings) {
            mapListingData(listing);
            if (!listing.listingKey || !listing.listingId) continue;
            pushListing(listing, false);
          }
        }
      } catch (err) {
        console.error('[MediaSync] fetchListings error:', err);
      }
    },

    // -----------------------------------------------------------------
    // fetchListingDetails — full listing detail for a selected card
    // -----------------------------------------------------------------
    fetchListingDetails: async (partialListing, pushListing) => {
      try {
        const listingKey = (partialListing.listingKey ??
          partialListing.ListingKey ??
          partialListing.listingId) as string;

        const details = await getListing(listingKey, { mediaV2: false });

        if (details.Property) {
          Object.assign(details, details.Property);
        }
        if (partialListing.resourceKey) {
          details.resourceKey = partialListing.resourceKey;
        }
        if (partialListing.__EditAsAgentKey__) {
          details.__EditAsAgentKey__ = partialListing.__EditAsAgentKey__;
        }

        mapListingData(details);
        pushListing(details);
      } catch (err) {
        console.error('[MediaSync] fetchListingDetails error:', err);
      }
    },

    // -----------------------------------------------------------------
    // importStart — prepare metadata before individual uploads begin.
    // Called by the plugin BEFORE it downloads files. Mirrors the
    // Keystone pattern of tagging each entry with order metadata.
    // -----------------------------------------------------------------
    importStart: async (mediaEntries, _listing, _context) => {
      try {
        let orderCount = 1;
        for (const entry of mediaEntries) {
          transform(entry);
          entry.metadata = {
            ...((entry.metadata as Record<string, unknown>) ?? {}),
            _ngwOrder: orderCount,
            _ngwCategory: toCategory(entry.mediaType as string | number),
          };
          orderCount++;
        }
      } catch (err) {
        console.error('[MediaSync] importStart error:', err);
      }
    },

    // -----------------------------------------------------------------
    // uploadMedia — upload a single media file to S3 via pre-signed URL.
    // Called by the plugin per-file AFTER it downloads the blob.
    //
    // Errors are intentionally NOT caught here. The plugin's
    // processMediaItem wraps this call in its own try/catch and surfaces
    // failures via its built-in error/retry modal.
    // -----------------------------------------------------------------
    uploadMedia: async (mediaEntry, listing, context) => {
      transform(mediaEntry);

      // Tour URLs are saved via a dedicated endpoint
      if (
        mediaEntry.mediaType === MEDIA_TYPES.TOUR_URL ||
        String(mediaEntry.mediaType).toUpperCase() === 'TOUR_URL'
      ) {
        await saveListingTour(
          listing.listingKey as string,
          (mediaEntry.description as string) || 'Virtual Tour',
          mediaEntry.tourUrl as string,
        );
        return;
      }

      const blob = mediaEntry.fileBlob as Blob | undefined;
      const metadata = (mediaEntry.metadata ?? {}) as Record<string, unknown>;
      const category =
        (metadata._ngwCategory as string) ??
        toCategory(mediaEntry.mediaType as string | number);

      // Request a pre-signed S3 upload URL from the NGW backend
      const uploads = await initiateMediaUpload([
        {
          fileName: mediaEntry.fileName as string,
          fileSize: blob?.size ?? 0,
          fileType: blob?.type ?? 'application/octet-stream',
          category,
          listingKey: listing.listingKey as string,
          listingId: listing.listingId as string,
          generateDescriptions:
            context?.suggestAIDescription === true ||
            mediaEntry?.suggestAIDescription === true,
        },
      ]);

      const uploadUrl = uploads?.[0]?.uploadUrl;
      if (!uploadUrl) {
        throw new Error(`No upload URL returned for ${mediaEntry.fileName}`);
      }

      // PUT the blob to S3
      if (blob) {
        const resp = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': blob.type || 'application/octet-stream' },
          body: blob,
        });
        if (!resp.ok) {
          throw new Error(`S3 upload failed with status ${resp.status}`);
        }
      }
    },

    // -----------------------------------------------------------------
    // importComplete — persist MediaBy and redirect to media center
    // -----------------------------------------------------------------
    importComplete: async (context) => {
      try {
        const listing = context.listing as Record<string, unknown>;
        const vendorName = context.vendorName as string | undefined;

        if (vendorName && listing.listingKey) {
          await saveListing('UPDATE', listing.listingKey as string, {
            Property: { MediaBy: vendorName },
          });
        }

        const url = window.location.href.split('?')[0];
        localStorage.setItem(
          'mediaSyncImportedListingKey',
          listing.listingKey as string,
        );
        setTimeout(() => {
          window.location.assign(
            `${url.replace(/\/media-sync.*$/, '')}/edit/${listing.listingKey}/media/photos?media_center_terms_accepted=true`,
          );
        }, 1000);
      } catch (err) {
        console.error('[MediaSync] importComplete error:', err);
      }
    },
  };
}
